import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MdVisibility, MdVisibilityOff, MdCheck } from 'react-icons/md';
import { validateEmail, validateRequired } from './validation';
import { t } from '../../utils/i18n';

export default function PasswordLoginForm({ onSubmit, onForgotPassword, isLoading, error, setError, hideRegisterLink = false, onRegisterClick = null }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [fieldErrors, setFieldErrors] = useState({});

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});

    const emailRes = validateEmail(email);
    const passRes = validateRequired(password, 'Lozinka');

    if (!emailRes.valid) {
      setFieldErrors((prev) => ({ ...prev, email: emailRes.message }));
      return;
    }
    if (!passRes.valid) {
      setFieldErrors((prev) => ({ ...prev, password: passRes.message }));
      return;
    }

    onSubmit(email, password, rememberMe);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-1.5 text-gray-900 dark:text-gray-100">
          {t('auth.login.emailLabel') || 'Email adresa'}
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setFieldErrors((prev) => ({ ...prev, email: null }));
            setError('');
          }}
          required
          autoComplete="email"
          className={`input-base ${fieldErrors.email ? 'input-error' : ''}`}
          placeholder={t('auth.login.emailPlaceholder') || 'vas@email.com'}
        />
        {fieldErrors.email && <p className="error-text">{fieldErrors.email}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium mb-1.5 text-gray-900 dark:text-gray-100">
          {t('auth.login.passwordLabel') || 'Lozinka'}
        </label>
        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setFieldErrors((prev) => ({ ...prev, password: null }));
              setError('');
            }}
            required
            autoComplete="current-password"
            className={`input-base pr-12 ${fieldErrors.password ? 'input-error' : ''}`}
            placeholder={t('auth.login.passwordPlaceholder') || '••••••••'}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 p-1 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
            aria-label={showPassword ? 'Sakrij lozinku' : 'Prikaži lozinku'}
          >
            {showPassword ? <MdVisibilityOff size={20} /> : <MdVisibility size={20} />}
          </button>
        </div>
        {fieldErrors.password && <p className="error-text">{fieldErrors.password}</p>}
      </div>

      <div className="flex items-center justify-between">
        <label className="flex items-center gap-3 cursor-pointer group select-none">
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            className="sr-only"
            aria-label="Zapamti me"
          />
          <span
            role="presentation"
            className={`flex items-center justify-center w-6 h-6 rounded-lg border-2 flex-shrink-0 transition-colors group-hover:border-primary-400 dark:group-hover:border-primary-500 ${
              rememberMe
                ? 'bg-primary-600 border-primary-600 dark:bg-primary-500 dark:border-primary-500 text-white'
                : 'border-gray-400 dark:border-gray-500 bg-white dark:bg-gray-700'
            }`}
          >
            {rememberMe && <MdCheck className="w-4 h-4" strokeWidth={3} />}
          </span>
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Zapamti me</span>
        </label>
        <button
          type="button"
          onClick={onForgotPassword}
          className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium focus:outline-none focus:ring-2 focus:ring-primary-500/50 rounded"
        >
          Zaboravljena lozinka?
        </button>
      </div>

      {error && <div className="p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">{error}</div>}

      <button
        type="submit"
        disabled={isLoading || !email || !password}
        className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {isLoading ? (
          <>
            <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
            {t('auth.login.loggingIn') || 'Prijava...'}
          </>
        ) : (
          t('auth.login.signIn') || 'Prijavi se'
        )}
      </button>

      {!hideRegisterLink && (
        <p className="text-sm text-center text-gray-600 dark:text-gray-400">
          Nemate nalog?{' '}
          {onRegisterClick ? (
            <button
              type="button"
              onClick={onRegisterClick}
              className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-semibold"
            >
              Registrujte se
            </button>
          ) : (
            <Link
              to="/register"
              className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-semibold"
            >
              Registrujte se
            </Link>
          )}
        </p>
      )}
    </form>
  );
}
